import { PhaseDefinition, TransitionDefinition } from '../types';

/**
 * Detects circular dependencies in phase transitions using Depth-First Search (DFS)
 * @param phases - Array of phase definitions
 * @param transitions - Array of transition definitions
 * @returns Array of phase IDs forming a cycle, or null if no cycle exists
 */
export function detectCircularDependencies(
  phases: PhaseDefinition[],
  transitions: TransitionDefinition[]
): string[] | null {
  // Build adjacency list (directed graph)
  const graph = new Map<string, string[]>();
  
  // Initialize graph with all phases
  phases.forEach(phase => {
    graph.set(phase.phaseId.toString(), []);
  });
  
  // Add edges from transitions
  transitions.forEach(transition => {
    const fromId = transition.fromPhaseId.toString();
    const toId = transition.toPhaseId.toString();
    
    if (graph.has(fromId)) {
      graph.get(fromId)!.push(toId);
    }
  });
  
  // DFS state tracking
  const visited = new Set<string>();
  const recStack = new Set<string>();
  const cyclePath: string[] = [];
  
  /**
   * DFS helper function to detect cycles
   */
  const dfs = (phaseId: string, path: string[]): boolean => {
    // Mark as visited and add to recursion stack
    visited.add(phaseId);
    recStack.add(phaseId);
    path.push(phaseId);
    
    // Get neighbors (phases this phase transitions to)
    const neighbors = graph.get(phaseId) || [];
    
    for (const neighborId of neighbors) {
      // If neighbor is in recursion stack, we found a cycle
      if (recStack.has(neighborId)) {
        // Build cycle path: from first occurrence of neighbor to current phase
        const cycleStartIndex = path.indexOf(neighborId);
        cyclePath.push(...path.slice(cycleStartIndex), neighborId);
        return true;
      }
      
      // If not visited, recurse
      if (!visited.has(neighborId)) {
        if (dfs(neighborId, path)) {
          return true;
        }
      }
    }
    
    // Remove from recursion stack (backtrack)
    recStack.delete(phaseId);
    path.pop();
    return false;
  };
  
  // Check all phases (handles disconnected components)
  for (const phase of phases) {
    const phaseIdStr = phase.phaseId.toString();
    if (!visited.has(phaseIdStr)) {
      if (dfs(phaseIdStr, [])) {
        return cyclePath;
      }
    }
  }
  
  return null;
}

/**
 * Formats cycle path for display
 * @param cyclePath - Array of phase IDs forming a cycle
 * @param phases - Array of phase definitions (for name lookup)
 * @returns Formatted string describing the cycle
 */
export function formatCyclePath(
  cyclePath: string[],
  phases: PhaseDefinition[]
): string {
  const phaseNames = cyclePath.map(phaseId => {
    const phase = phases.find(p => p.phaseId.toString() === phaseId);
    return phase ? phase.name : phaseId;
  });
  
  return phaseNames.join(' → ');
}

/**
 * Validates transitions for common issues
 * @param phases - Array of phase definitions
 * @param transitions - Array of transition definitions
 * @returns Array of validation error messages
 */
export function validateTransitions(
  phases: PhaseDefinition[],
  transitions: TransitionDefinition[]
): string[] {
  const errors: string[] = [];
  const phaseIds = new Set(phases.map(p => p.phaseId));
  
  // Check for invalid phase references
  transitions.forEach(transition => {
    if (!phaseIds.has(transition.fromPhaseId)) {
      errors.push(`Transition references invalid "from" phase: ${transition.fromPhaseId}`);
    }
    if (!phaseIds.has(transition.toPhaseId)) {
      errors.push(`Transition references invalid "to" phase: ${transition.toPhaseId}`);
    }
    if (transition.fromPhaseId === transition.toPhaseId) {
      errors.push(`Self-transition detected: phase cannot transition to itself`);
    }
  });
  
  // Check for circular dependencies
  const cycle = detectCircularDependencies(phases, transitions);
  if (cycle) {
    const cycleNames = formatCyclePath(cycle, phases);
    errors.push(`Circular dependency detected: ${cycleNames}`);
  }
  
  return errors;
}

